import {
  Typography,
  Box,
  useTheme,
  useMediaQuery,
  styled,
} from "@mui/material";
import { scaleLinear } from "@visx/scale";
import { extent } from "d3-array";
import { Circle, LinePath } from "@visx/shape";
import { Group } from "@visx/group";
import { Grid } from "@visx/grid";
import { Axis, AxisLeft } from "@visx/axis";
import { Threshold } from "@visx/threshold";
import { Annotation, Label } from "@visx/annotation";
import { useTooltip, TooltipWithBounds, defaultStyles } from "@visx/tooltip";
import React, { useRef, useMemo, useCallback } from "react";
import { localPoint } from "@visx/event";
import { voronoi } from "@visx/voronoi";
import { formatPercent } from "../../../utils";
import { colors } from "../../../consts";
import AverageLine from "../averageLine";
import { ParentSize } from "@visx/responsive";
import _ from "lodash";
// import { curveNatural } from "@visx/curve";

const ScatterChartStyled = styled(Box)(({ theme }) => ({
  position: "relative",
  background: theme.palette.gray,
  "& .visx-axis-tick text": {
    // fontFamily: "GT-Eesti-Text-Bold",
    fontFamily: "Gotham-Bold",
    fill: theme.palette.grayDark,
    fontSize: "12px",
  },
  "& .visx-axis-label": {
    ...theme.typography.chartAxisLabel,
    color: "#696969 !important",
  },
  "& .visx-axis-line": {
    stroke: "#fff",
    strokeWidth: 4,
  },
  "& .color-legend .visx-line": {
    stroke: "#696969",
    strokeWidth: 2,
    transform: "scaleY(0.6)",
  },
  "& .visx-annotationlabel text": {
    ...theme.typography.chartAxisLabel,
    fontSize: "15px !important",
  },
  "& .visx-tooltip": {
    zIndex: 10,
  },
  "& .tooltip-value-box": {
    display: "flex",
    marginTop: theme.spacing(0.5),
    "& .MuiTypography-root": {
      lineHeight: 1,
    },
    "& > .MuiTypography-root": {
      marginTop: "auto",
      marginBottom: "auto",
    },
    "& .tooltip-error-bands-box": {
      marginLeft: theme.spacing(1),
      flexDirection: "column",
      display: "flex",
      "& .MuiTypography-root": {
        fontSize: "12px",
        "&.tooltip-min": {
          marginBottom: theme.spacing(0.25),
        },
      },
    },
  },
}));

const getStroke = ({ stroke, label }) =>
  !!stroke ? stroke : !!label ? (stroke = "black") : (stroke = "none");

const focusHighlightingDefaults = {
  points: [0.7, 0.9, 0.3],
  lines: [0.6, 0.9, 0.3],
  bands: [0.05, 0.15, 0],
};

const getFocusBasedValue = ({
  values: [defaultVal, focusedVal, unfocusedVal],
  element,
  focused,
  propertiesToMatch = ["groupId"],
}) => {
  if (!focused) return defaultVal;
  return propertiesToMatch.every(p => focused[p] === element[p])
    ? focusedVal
    : unfocusedVal;
};

const TooltipLabel = ({ children }) =>
  !children ? null : <span className="tooltip-label">{children}: </span>;

// adapted from https://codesandbox.io/s/objective-snow-txbsr?file=/src/worldBankData.js:0-23301
const ScatterChart = ({
  children,
  pointArrays,
  lineArrays = [[]],
  errorBandArrays = [[]],
  width = 800,
  height = 500,
  xAxisLabel = "",
  yAxisLabel = "",
  yAxisLabelOffset = 50,
  xTooltipLabel,
  yTooltipLabel,
  xTooltipValueVariant = "chartTooltipValue",
  yTooltipValueVariant = "chartTooltipValue",
  xFormatter = formatPercent,
  xAxisFormatter,
  yFormatter = formatPercent,
  yAxisFormatter,
  xTicks = 5,
  xTickValues,
  yTicks = 5,
  pointSize = 7,
  mobilePointSize,
  xMin,
  yMin = 0,
  xMax,
  yMax,
  useFocusHighlighting,
  focusHighlightingOverrides = {},
  curve,
  showXEqYLine,
  errorBandsOpacity = 0.1,
  lineOpacity = 0.7,
  pointOpacity = 0.9,
  useThemeColors,
  tooltipAdditions = [],
  showGrid = false,
  gridOptions = {},
  hideYAxis,
  disableTooltip,
  margin = { top: 20, left: 80, right: 40, bottom: 80 },
}) => {
  const focusHighlightingValues = {
    ...focusHighlightingDefaults,
    ...focusHighlightingOverrides,
  };

  const allPoints = _.flatMap(pointArrays);

  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;

  const xAccessor = d => d.x;
  const yAccessor = d => d.y;
  // const radius = d => d.population;

  // extents are based on points, not lines or error bands (can be overridden with x/yMin/Max)
  const xExtents = extent(allPoints, xAccessor);
  if (xMin !== undefined) xExtents[0] = xMin;
  if (xMax !== undefined) xExtents[1] = xMax;
  const xScale = scaleLinear({
    range: [0, innerWidth],
    domain: xExtents,
  });

  const yExtents = extent(allPoints, yAccessor);
  if (yMin !== undefined) yExtents[0] = yMin;
  if (yMax !== undefined) yExtents[1] = yMax;
  const yScale = scaleLinear({
    range: [innerHeight, 0],
    domain: yExtents,
    nice: true,
  });

  // const rScale = scaleSqrt({
  //   range: [3, 30],
  //   domain: extent(allPoints, radius),
  // });

  // Tooltip handlers
  const {
    showTooltip,
    hideTooltip,
    tooltipData,
    tooltipOpen,
    tooltipTop = 0,
    tooltipLeft = 0,
  } = useTooltip();

  // tooltip is based on points, not lines or error bands
  const voronoiLayout = useMemo(
    () =>
      voronoi({
        x: d => xScale(xAccessor(d)) + margin.left ?? 0,
        y: d => yScale(yAccessor(d)) + margin.top ?? 0,
        width,
        height,
      })(allPoints),
    [allPoints, width, height, xScale, yScale]
  );

  let tooltipTimeout;
  const svgRef = useRef(null);

  const handleMouseMove = useCallback(
    event => {
      if (disableTooltip) return;
      if (tooltipTimeout) clearTimeout(tooltipTimeout);
      if (!svgRef.current) return;

      // find the nearest polygon to the current mouse position
      const point = localPoint(svgRef.current, event);
      if (!point) return;
      const neighborRadius = 100;
      const closest = voronoiLayout.find(point.x, point.y, neighborRadius);
      if (closest) {
        showTooltip({
          tooltipLeft: xScale(xAccessor(closest.data)) + margin.left,
          tooltipTop: yScale(yAccessor(closest.data)) + margin.top,
          tooltipData: closest.data,
        });
      }
    },
    [xScale, yScale, showTooltip, voronoiLayout, tooltipTimeout]
  );

  const handleMouseLeave = useCallback(() => {
    tooltipTimeout = window.setTimeout(() => {
      hideTooltip();
    }, 500);
  }, [hideTooltip]);

  const getColor = (dataArrIdx, d = {}) => {
    // use color if specified by data
    if (d.color) return d.color;
    // if we're using theme colors, choose based on the array index
    if (useThemeColors && _.isNumber(dataArrIdx))
      return colors.palette[dataArrIdx];
    // default secondary theme color
    return colors.palette[1];
  };

  const theme = useTheme();
  pointSize =
    useMediaQuery(theme.breakpoints.down("sm")) && mobilePointSize
      ? mobilePointSize
      : pointSize;

  // CHART ELEMENT GETTERS
  const showErrorBands = _.get(errorBandArrays, [0, "length"]) > 0;
  const getErrorBands = () =>
    !!showErrorBands && (
      <Group className="error-bands">
        {errorBandArrays.map((data, i) => (
          <Threshold
            key={`threshold-${data[0].name}`}
            id={`${data[0].name}-threshold`}
            data={data}
            curve={curve}
            x={d => xScale(d.x)}
            y0={d => yScale(d.min)}
            y1={d => yScale(d.max)}
            clipBelowTo={innerHeight}
            // NOTE: resolves a warning, omit if any bugs are introduced
            clipAboveTo={0}
            belowAreaProps={{
              fill: getColor(i, data[0]),
              fillOpacity: !useFocusHighlighting
                ? errorBandsOpacity
                : getFocusBasedValue({
                    values: focusHighlightingValues.bands,
                    element: data[0],
                    focused: tooltipData,
                  }),
            }}
            aboveAreaProps={{
              fill: "none",
            }}
          />
        ))}
      </Group>
    );

  const showLines = _.get(lineArrays, [0, "length"]) > 0;
  const getLines = () =>
    !!showLines && (
      <Group className="lines">
        {lineArrays.map((data, i) => (
          <LinePath
            key={"LinePath-" + i}
            data={data}
            z="1000"
            curve={curve}
            x={d => xScale(d.x)}
            y={d => yScale(d.lineY || d.y)}
            stroke={getColor(i, data[0])}
            strokeWidth={3}
            strokeOpacity={
              !useFocusHighlighting
                ? lineOpacity
                : getFocusBasedValue({
                    values: focusHighlightingValues.lines,
                    element: data[0],
                    focused: tooltipData,
                  })
            }
          />
        ))}
      </Group>
    );

  const getPoint = (d, pointArrIdx, keySuffix) => (
    <Circle
      key={"circle-" + pointArrIdx + keySuffix}
      cx={xScale(xAccessor(d))}
      cy={yScale(yAccessor(d))}
      r={pointSize}
      // r={rScale(radius(d))}
      // fill={colorScale(color(d))}
      fill={getColor(pointArrIdx, d)}
      fillOpacity={
        !useFocusHighlighting
          ? pointOpacity
          : getFocusBasedValue({
              values: focusHighlightingValues.points,
              element: d,
              focused: tooltipData,
              propertiesToMatch: ["groupId", "x"],
            })
      }
      strokeWidth={0}
      // stroke={tooltipData === d ? "black" : "none"}
      stroke={getStroke(d)}
    />
  );

  const getPoints = () => (
    <Group className="points">
      {pointArrays.map((data, i) => (
        <React.Fragment key={`point-array-${i}`}>
          {data
            .filter(point => !point.label)
            .map((point, j) => getPoint(point, i, `${i}-${j}`))}
          {/* make sure labeled points are above the others in case of overlap */}
          {data
            .filter(point => !!point.label)
            .map((point, j) => getPoint(point, i, `${i}-${j}-L`))}
        </React.Fragment>
      ))}
    </Group>
  );

  const getLabels = () => (
    <Group className="labels">
      {allPoints
        .filter(d => !!d.label)
        .map((d, i) => (
          <Annotation
            key={"label-" + i}
            x={xScale(xAccessor(d))}
            y={yScale(yAccessor(d))}
            // default label position is just left of point, but can be customized
            dx={d.label.dx || -1}
            dy={d.label.dy || 0}
          >
            <Label
              backgroundFill="none"
              showAnchorLine={false}
              title={d.name}
              titleFontWeight={400}
              // titleProps={theme.typography.body2}
            />
          </Annotation>
        ))}
    </Group>
  );

  if (innerWidth <= 0 || innerHeight <= 0) return null;
  return (
    <ScatterChartStyled>
      {/* include in actual blog post for now */}
      {/* {title && <Typography variant="h3">{title}</Typography>} */}
      <svg width={width} height={height} ref={svgRef}>
        <Group
          left={margin.left}
          top={margin.top}
          width={innerWidth}
          height={innerHeight}
        >
          <rect
            fill="transparent"
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            onTouchMove={handleMouseMove}
            onTouchEnd={handleMouseLeave}
            width={innerWidth}
            height={innerHeight}
          />
          {showGrid && (
            <Grid
              xScale={xScale}
              yScale={yScale}
              width={innerWidth}
              height={innerHeight}
              stroke={"white"}
              style={{ border: "2px solid blue" }}
              strokeWidth={2}
              {...gridOptions}
            />
          )}
          {!hideYAxis && (
            <AxisLeft
              scale={yScale}
              label={yAxisLabel}
              labelOffset={yAxisLabelOffset}
              tickFormat={yAxisFormatter || yFormatter}
              hideTicks={true}
              numTicks={yTicks}
            />
          )}
          <Axis
            orientation="bottom"
            scale={xScale}
            top={innerHeight}
            labelOffset={21}
            hideTicks={true}
            // left={margin.left}
            tickFormat={xAxisFormatter || xFormatter}
            numTicks={xTicks}
            tickValues={xTickValues}
            label={xAxisLabel}
          />
          <Group className="plotted-elements" pointerEvents="none">
            {showXEqYLine && (
              <AverageLine
                width={innerWidth}
                y0={yScale(0)}
                y1={yScale(xScale.invert(innerWidth))}
              />
            )}
            {getErrorBands()}
            {getPoints()}
            {getLines()}
            {getLabels()}
          </Group>
        </Group>
      </svg>
      {!disableTooltip &&
        tooltipOpen &&
        tooltipData &&
        tooltipLeft != null &&
        tooltipTop != null && (
          <TooltipWithBounds
            left={tooltipLeft + 10}
            top={tooltipTop + 10}
            style={{
              ...defaultStyles,
              backgroundColor: "black",
              padding: "12px",
              color: "white",
            }}
          >
            {!!tooltipData.name && (
              <Typography
                className="tooltip-name"
                variant="chartTooltipHeading"
                sx={{
                  display: "inline-block",
                  mb: 1,
                }}
              >
                {tooltipData.name}
              </Typography>
            )}
            <Typography className="tooltip-x" variant={xTooltipValueVariant}>
              <TooltipLabel>{xTooltipLabel}</TooltipLabel>
              {xFormatter(xAccessor(tooltipData))}
            </Typography>
            <Box className="tooltip-value-box">
              <Typography className="tooltip-y" variant={yTooltipValueVariant}>
                <TooltipLabel>{yTooltipLabel}</TooltipLabel>
                {yFormatter(yAccessor(tooltipData))}
              </Typography>
              {showErrorBands && tooltipData.min && tooltipData.max && (
                <Box className="tooltip-error-bands-box">
                  <Typography
                    className="tooltip-min"
                    variant={yTooltipValueVariant}
                  >
                    <TooltipLabel>min</TooltipLabel>
                    {yFormatter(tooltipData.min)}
                  </Typography>
                  <Typography variant={yTooltipValueVariant}>
                    <TooltipLabel>max</TooltipLabel>
                    {yFormatter(tooltipData.max)}
                  </Typography>
                </Box>
              )}
            </Box>
            <Box>
              {!!tooltipAdditions.length &&
                tooltipAdditions.map(
                  ({
                    field,
                    label,
                    formatter,
                    variant = yTooltipValueVariant,
                  }) => (
                    <Typography variant={variant} key={`TA-${field}-${label}`}>
                      <TooltipLabel>{label}</TooltipLabel>
                      {formatter(tooltipData[field])}
                    </Typography>
                  )
                )}
            </Box>
          </TooltipWithBounds>
        )}
      {children}
    </ScatterChartStyled>
  );
};

const ScatterChartWrapper = props => (
  <ParentSize>
    {({ width, height }) => (
      <ScatterChart width={width} height={height} {...props} />
    )}
  </ParentSize>
);

export default ScatterChartWrapper;
